import React, { ChangeEvent, ReactElement, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { compose } from '../../node_module_clones/recompose';
import classNames from 'classnames';
import { DropTargetMonitor } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import * as Immutable from 'immutable';

// Component
import ElementDropTarget from '../element/dnd/elementDropTargets/ElementDropTarget';
import ElementIconBase from '../components/elementIcon/ElementIcon';
import DraggableElement from '../element/dnd/DraggableElement';
import IconTool from '../workspace/toolbar/components/selectionTools/iconTools/IconTool';

// Utils
import favouritesSidebarDropLocationFn from './favouritesSidebarDropLocationFn';
import makeCanDropFn from '../element/dnd/canDrop/makeCanDropFn';
import onlyAllowBoardPredicate from '../element/dnd/canDrop/onlyAllowBoardPredicate';
import { getIcon, getTitle } from '../../common/elements/utils/elementPropertyUtils';
import { activePopupSelector } from '../components/popupPanel/popupOpenSelector';

// Actions
import { navigateToBoard } from '../reducers/navigationActions';
import { updateElement } from '../element/actions/elementActions';

// Constants
import { DROP_TARGET_TYPES } from '../../common/elements/elementConstants';
import { ImMNElement } from '../../common/elements/elementModelTypes';
import { PopupIds } from '../components/popupPanel/popupConstants';

// Styles
import './FavouriteBoardSidebarItem.scss';

// Types
type FavouriteBoardSidebarItemProps = {
    currentBoardId: string;
    elementId: string;
    connectDropTarget?: RefObject<HTMLDivElement>;
    element?: ImMNElement;
    isHovered: boolean;
    connectDragSource: (element: ReactElement) => ReactElement;
    disableDragPreview?: boolean;
    gridSize: number;
};

type FavouriteSidebarItemContentProps = {
    element: ImMNElement;
    gridSize: number;
    title: string;
};

type EditableFavouriteSidebarItemContentProps = {
    element: ImMNElement;
    gridSize: number;
    elementId: string;
    inputRef: RefObject<HTMLInputElement>;
};

// Configuration
const sidebarDropTargetConfig = {
    drop: favouritesSidebarDropLocationFn,
    canDrop: makeCanDropFn([onlyAllowBoardPredicate]),
    hover: () => {},
    hoverType: DROP_TARGET_TYPES.WORKSPACE,
    collect: (monitor: DropTargetMonitor) => ({
        isHovered: monitor.canDrop() && monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
    }),
};

// Components
const FavouriteSidebarItemContent = ({ element, title }: FavouriteSidebarItemContentProps) => (
    <>
        <div className="sidebar-icon">
            <ElementIconBase icon={getIcon(element)} isDarkBackground={false} />
        </div>
        <div className="SidebarItemTitle">{title}</div>
    </>
);

const EditableFavouriteSidebarItemContent = ({
    element,
    gridSize,
    elementId,
    inputRef,
}: EditableFavouriteSidebarItemContentProps) => {
    const [localTitle, setLocalTitle] = useState(getTitle(element));
    const dispatch = useDispatch();

    const dispatchUpdateTitle = useCallback(
        debounce((title) => {
            if (!title || !title.length) return;
            dispatch(updateElement({ id: elementId, changes: { title } }));
        }, 300),
        [elementId],
    );

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newTitle = e.target.value;
        setLocalTitle(newTitle);
        dispatchUpdateTitle(newTitle);
    };

    return (
        <>
            <div className="sidebar-icon">
                <IconTool selectedElements={Immutable.List([element])} gridSize={gridSize} />
            </div>
            <input
                ref={inputRef}
                className="sidebar-title-input"
                type="text"
                value={localTitle}
                onChange={handleTitleChange}
            />
        </>
    );
};

// Main component
const FavouriteBoardSidebarItem = ({
    currentBoardId,
    elementId,
    connectDropTarget,
    element,
    isHovered,
    connectDragSource,
    gridSize,
}: FavouriteBoardSidebarItemProps): ReactElement | null => {
    if (!element) return null;

    const inputRef = useRef<HTMLInputElement>(null);
    const boardItemRef = useRef<HTMLDivElement>(null);
    const activePopup = useSelector(activePopupSelector);
    const [isEditing, setIsEditing] = useState(false);
    const dispatch = useDispatch();
    const isDoubleClick = useRef(false);

    const handleClick = () => {
        if (isDoubleClick.current) {
            isDoubleClick.current = false;
            return;
        }

        setTimeout(() => {
            if (!isDoubleClick.current && !isEditing) {
                dispatch(navigateToBoard({ boardId: elementId }));
            }
        }, 200);
    };

    const handleDoubleClick = () => {
        isDoubleClick.current = true;
        setIsEditing(true);
    };

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isEditing &&
                boardItemRef.current &&
                !boardItemRef.current.contains(event.target as Node) &&
                !activePopup.has(PopupIds.ICON)
            ) {
                setIsEditing(false);
            }
        };

        if (isEditing) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isEditing, activePopup]);

    return connectDragSource(
        <div ref={connectDropTarget} className="FavouriteBoardSidebarItem">
            <div
                ref={boardItemRef}
                className={classNames('BoardItem', {
                    active: currentBoardId === elementId,
                    hover: isHovered,
                    editing: isEditing,
                })}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
            >
                {isEditing ? (
                    <EditableFavouriteSidebarItemContent
                        element={element}
                        gridSize={gridSize}
                        elementId={elementId}
                        inputRef={inputRef}
                    />
                ) : (
                    <FavouriteSidebarItemContent element={element} gridSize={gridSize} title={getTitle(element)} />
                )}
            </div>
        </div>,
    );
};

const EnhancedComponent = compose<FavouriteBoardSidebarItemProps, FavouriteBoardSidebarItemProps>(
    ElementDropTarget(sidebarDropTargetConfig),
    DraggableElement,
)(FavouriteBoardSidebarItem);

EnhancedComponent.defaultProps = {
    disableDragPreview: true,
};

export default EnhancedComponent;
